import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import { IMonitoringService } from '@desygner/ui-core-monitoring';
import {
  type IWorkflowsService,
  type Job,
  type JobResponse,
  TYPES as WORKSPACE_SERVICE_TYPES,
} from '@desygner/ui-data-access-workspace';
import {
  TYPES as PROJECTS_STORES_TYPES,
  ProjectStoreInjector,
} from '@desygner/ui-features-projects';

import { TYPES } from '../../inversify.types';
import { IUserStoreInjector, IViewerStoreInjector, IWorkspaceStoreInjector } from '../../store';

export interface IWorkflowManager {
  isEnabled: () => boolean;
  init: () => void;
  createJob: (action: string) => void;
}

@injectable()
export class WorkflowManager implements IWorkflowManager {
  private limit = 20;

  constructor(
    @inject(TYPES.Store.Workspace)
    private readonly workspaceStore: IWorkspaceStoreInjector,
    @inject(TYPES.Store.User)
    private readonly userStore: IUserStoreInjector,
    @inject(TYPES.MonitoringService) private monitoringService: IMonitoringService,
    @inject(WORKSPACE_SERVICE_TYPES.WorkflowsService)
    private workflowsService: IWorkflowsService,
    @inject(TYPES.Store.Viewer)
    private viewerStore: IViewerStoreInjector,
    @inject(PROJECTS_STORES_TYPES.ProjectsStore)
    private projectsStore: ProjectStoreInjector
  ) {}

  private isWorkspace() {
    return this.viewerStore.getState().company?.is_workspace ?? false;
  }

  public isEnabled() {
    return Boolean(
      this.isWorkspace() &&
        this.workspaceStore.getState().company.data?.add_ons.includes('workflow')
    );
  }

  private filterFinishedJobs(job: Job): boolean {
    return job.status !== 'finished' || !job.output.is_ready;
  }

  private async loadJobs(limit?: number) {
    const { setJobs } = this.workspaceStore.getState().workflow;
    const jobs = await this.getJobs(limit ?? this.limit);

    if (!jobs) return;

    setJobs(
      jobs
        .map((job) => ({
          output: job.outputs[0],
          status: job.state,
          ...job,
        }))
        .filter(this.filterFinishedJobs.bind(this))
    );
  }

  public async init() {
    if (!this.isEnabled()) return;

    this.loadJobs();
  }

  public async createJob(action: string) {
    const temporaryId = Date.now();
    const { appendJob, updateLatestJobByAction } = this.workspaceStore.getState().workflow;
    const { project } = this.projectsStore.getState();
    const { project: viewerProject } = this.viewerStore.getState();
    const { membership } = this.userStore.getState();

    appendJob({
      id: temporaryId,
      status: 'open',
      output: {
        output_id: temporaryId,
        output_action: action,
        is_ready: false,
        is_blocked: false,
      },
      updated_at: new Date().toISOString(),
    });

    const alias = viewerProject.type === 'template' ? 'brand_template' : 'brand_design';

    if (!project?.currentProject?.id) return;

    try {
      const { data } = await this.workflowsService.createJob({
        assignToId: (membership && membership.id) || 0,
        outputId: project.currentProject.id,
        outputType: alias,
        action,
      });

      if (!data) return;

      updateLatestJobByAction(action, {
        id: data.id,
        status: data.state,
        output: data.outputs[0],
        updated_at: data.updated_at,
      });

      return;
    } catch (error) {
      this.monitoringService.logException(error as Error);
      return;
    }
  }

  private async getJobs(limit = 20): Promise<JobResponse[]> {
    const { project } = this.projectsStore.getState();
    const { project: viewerProject } = this.viewerStore.getState();
    const alias = viewerProject.type === 'template' ? 'brand_template' : 'brand_design';

    if (!project?.currentProject?.id) return [];

    try {
      const response = await this.workflowsService.getJobs({
        projectId: project.currentProject.id,
        alias,
        limit,
      });

      const jobs = response.data;

      if (!jobs || !Array.isArray(jobs)) return [];

      return jobs.sort((a, b) => {
        const firstDate = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const secondDate = b.updated_at ? new Date(b.updated_at).getTime() : 0;

        return secondDate - firstDate;
      });
    } catch (error) {
      this.monitoringService.logException(error as Error);

      return [];
    }
  }
}
